import { staticError, useGuardedEffect } from "@p0security/frontend-core";
import { DB, FirestoreDoc } from "@p0security/frontend-core/react";
import {
  MonitorRanking,
  priorityLabels,
} from "@p0security/shared/assessment/constants";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { ALL_SCOPE_SENTINEL, toKey } from "@p0security/shared/types/assessment";
import {
  type TargetNodeType,
  TargetNodeTypes,
} from "@p0security/shared/types/assessment/data/index";
import {
  Finding,
  FindingState,
} from "@p0security/shared/types/assessment/finding";
import { Monitor } from "@p0security/shared/types/assessment/monitor";
import { mapWith } from "@p0security/util/collections";
import { throttledAllSettled } from "@p0security/util/throttle";
import { isFulfilled, isa } from "@p0security/util/types/is";
import { EnvironmentDocsContext } from "components/Environment/contexts/EnvironmentDocsContext";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { endOfDay, startOfDay } from "date-fns";
import {
  QueryConstraint,
  collection,
  getCountFromServer,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useObjectMemo } from "hooks/useObjectMemo";
import { noop, orderBy, partition } from "lodash";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router";
import {
  ArrayParam,
  BooleanParam,
  DateTimeParam,
  type QueryParamConfig,
  StringParam,
  createEnumParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { Tenant } from "../../Login";
import { useMonitors } from "./MonitorsContext";
import { ScopeContext } from "./ScopeContext";

export type MonitorWithMetadata = Monitor & {
  monitorId: string;
  isCustom?: boolean;
  archived?: boolean;
  autoAssign?: boolean;
};

export type MonitorWithCount = MonitorWithMetadata & {
  count: number;
};

export type MonitorWithFindings = MonitorWithMetadata & {
  /** All findings for the current findings select */
  scopedFindings: FirestoreDoc<Finding>[];
  /** Findings for the current select, but for all possible scopes */
  countsByScope: Record<string, number>;
};

export type AssessmentFindings = {
  /** Mapping of monitor ID to corresponding finding counts */
  allMonitors: Record<string, MonitorWithCount>;
  archived: MonitorWithCount[];
  findingParams: string;
  /** Combined loading state for both monitors and findings */
  loading: boolean;
  /** Returns whether the currently selected scope matches the monitor scope */
  isValidMonitorScope: boolean;
  /** If present, filters displayed monitors */
  monitorFilter: string;
  /** Priority-sorted list of monitors, filtered by findings select */
  prioritized: MonitorWithCount[];
  range: DateRange;
  selectedMonitor: MonitorWithFindings | undefined;
  /** Updates the monitor filter */
  setAssignment: (value: string) => void;
  setMonitorFilter: (term: string) => void;
  setRange: (range: DateRange) => void;
  setState: (value: string) => void;
  setTrigger: (value: string) => void;
  assignment: "all" | "unassigned";
  state: FindingState;
  trigger: "all" | "new";
  /** Query-param-backed monitor filters (type/fix/source). */
  filterParams: FindingsFilterParams;
  setFilterParams: (params: Partial<FindingsFilterParams>) => void;

  refresh: () => void;
};

export type FindingsFilterParams = {
  type: TargetNodeType[];
  fix: boolean;
  source: "all" | "custom" | "preset";
};

const DEFAULT_FILTER_PARAMS: FindingsFilterParams = {
  type: [],
  fix: false,
  source: "all",
};

type DateRange = [Date | undefined, Date | undefined];

// Frontend grinds to a halt if we ever load more than these
export const MAX_FINDINGS = 1e3;

const defaultFindingsContextValue: AssessmentFindings = {
  allMonitors: {},
  archived: [],
  findingParams: "",
  isValidMonitorScope: true,
  loading: true,
  monitorFilter: "",
  prioritized: [],
  range: [undefined, undefined],
  selectedMonitor: undefined,
  setMonitorFilter: noop,
  setRange: noop,
  setState: noop,
  setTrigger: noop,
  setAssignment: noop,
  assignment: "all",
  state: "open",
  trigger: "all",
  filterParams: DEFAULT_FILTER_PARAMS,
  setFilterParams: noop,

  refresh: noop,
};

export const FindingsContext = createContext<AssessmentFindings>(
  defaultFindingsContextValue
);

export const findingsQuery = (
  config: {
    lastJob: string;
    assessmentPath: string;
    monitorId: string;
    scopeKey: string;
    state: string;
    trigger: "all" | "new";
    assignment: "all" | "unassigned";
    start: Date | undefined;
    end: Date | undefined;
  },
  ...extra: QueryConstraint[]
) => {
  const constraints: QueryConstraint[] = [
    where("monitorId", "==", config.monitorId),
    where("state", "==", config.state),
    ...(config.scopeKey !== ALL_SCOPE_SENTINEL
      ? [where("scopeKey", "==", config.scopeKey)]
      : []),
    ...(config.trigger !== "all"
      ? [
          where("trigger.type", "==", config.trigger),
          where("trigger.jobId", "==", config.lastJob),
        ]
      : []),
    ...(config.assignment !== "all" ? [where("assignment", "==", null)] : []),
    ...(config.start
      ? [where("trigger.at", ">=", startOfDay(config.start).getTime())]
      : []),
    ...(config.end
      ? [where("trigger.at", "<=", endOfDay(config.end).getTime())]
      : []),
  ];
  return query(
    collection(DB, config.assessmentPath, "findings"),
    ...constraints,
    ...extra
  );
};

const isInRange = (
  trigger: Date,
  start: Date | null | undefined,
  end: Date | null | undefined
) =>
  // start & end are day-aligned, so broaden range to day starts and day ends
  (!start || trigger >= startOfDay(start)) &&
  (!end || trigger <= endOfDay(end));

export const FindingsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const value = useMemo(
    () => ({
      ...defaultFindingsContextValue,
      loading: false,
    }),
    []
  );
  const fallback = (
    <FindingsContext.Provider value={value}>
      {children}
    </FindingsContext.Provider>
  );

  const { hasEnvironments } = useContext(EnvironmentDocsContext);
  return hasEnvironments ? (
    <ErrorBoundary fallback={fallback}>
      <Provider>{children}</Provider>
    </ErrorBoundary>
  ) : (
    fallback
  );
};

const Provider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { details, last } = useContext(SelectedEnvironmentContext);
  const { scopeKey, validScopeKeys } = useContext(ScopeContext);
  const tenantId = useContext(Tenant);
  const { monitorId: selectedMonitorId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [allMonitors, setAllMonitors] = useState<
    Record<string, MonitorWithCount>
  >({});
  const [monitorFilter, setMonitorFilter] = useState("");
  const [filterQuery, setFilterQuery] = useQueryParams(
    {
      type: withDefault(ArrayParam, [] as TargetNodeType[]),
      fix: withDefault(BooleanParam, false),
      source: withDefault(
        createEnumParam(["all", "custom", "preset"]),
        "all"
      ) as QueryParamConfig<"all" | "custom" | "preset">,
    },
    { removeDefaultsFromUrl: true }
  );
  const setFilterParams = useCallback(
    (params: Partial<FindingsFilterParams>) => setFilterQuery(params),
    [setFilterQuery]
  );
  // Validate decoded params against their unions so malformed URLs can't inject
  // unknown values into filtering (e.g. an old saved link with a stale type).
  const filterParams = useMemo(
    (): FindingsFilterParams => ({
      type: ((filterQuery.type ?? []) as (string | null)[]).filter(
        (t): t is TargetNodeType =>
          TargetNodeTypes.includes(t as TargetNodeType)
      ),
      fix: filterQuery.fix ?? false,
      source: filterQuery.source ?? "all",
    }),
    [filterQuery.type, filterQuery.fix, filterQuery.source]
  );

  const [selectedMonitor, setSelectedMonitor] = useState<MonitorWithFindings>();
  const [refreshRequested, setRefreshRequested] = useState({});

  const refresh = useCallback(() => {
    setRefreshRequested({});
  }, []);

  const assessmentId = details?.assessmentId;

  const [trigger, setTrigger] = useQueryParam(
    "trigger",
    withDefault(StringParam, "all")
  );
  // Validate trigger query param, defaulting to "all"
  const selectedTrigger = useMemo(
    () => (trigger === "new" ? "new" : "all"),
    [trigger]
  );

  const [assignment, setAssignment] = useQueryParam(
    "assignment",
    withDefault(StringParam, "all")
  );
  // Validate assignment query param, defaulting to "all"
  const selectedAssignment = useMemo(
    () => (assignment === "unassigned" ? "unassigned" : "all"),
    [assignment]
  );

  const [start, setStart] = useQueryParam("start", DateTimeParam);
  const [end, setEnd] = useQueryParam("end", DateTimeParam);
  const [state, setState] = useQueryParam(
    "state",
    withDefault(StringParam, "open")
  );
  const setRange = useCallback(
    ([start, end]: DateRange) => {
      setStart(start);
      setEnd(end);
    },
    [setEnd, setStart]
  );

  const findingParams = `state=${state}&trigger=${trigger}&assignment=${assignment}${
    scopeKey !== ALL_SCOPE_SENTINEL ? `&scope=${scopeKey}` : ""
  }`;

  const { monitors, loading: monitorsLoading } = useMonitors();

  const isValidMonitorScope = useMemo(
    () =>
      selectedMonitor?.scopes?.includes(ALL_SCOPE_SENTINEL) ||
      (scopeKey !== ALL_SCOPE_SENTINEL && validScopeKeys.has(scopeKey)),
    [scopeKey, selectedMonitor?.scopes, validScopeKeys]
  );

  const assessmentPath = assessmentId
    ? toAssessmentPath(tenantId, assessmentId)
    : "";

  useGuardedEffect(
    (cancellation) => async () => {
      if (!assessmentId) return;

      setIsLoading(true);

      const beforeAllMonitors = performance.now();

      const loadFindingCounts = async (monitorId: string, scopeKey: string) =>
        (
          await getCountFromServer(
            findingsQuery({
              lastJob: last.doc?.data.jobId ?? "",
              monitorId,
              state,
              scopeKey,
              assessmentPath,
              trigger: selectedTrigger,
              assignment: selectedAssignment,
              start: start ?? undefined,
              end: end ?? undefined,
            })
          )
        ).data().count;

      const byMonitorPromises = Object.entries(monitors ?? {}).map(
        async ([id, monitor]) => {
          const count = monitor.archived
            ? 0
            : await loadFindingCounts(id, scopeKey);
          return [
            id,
            {
              ...monitor,
              count,
              monitorId: id,
            },
          ] as const;
        }
      );
      const monitorsWithCounts: Record<string, MonitorWithCount> =
        Object.fromEntries(await Promise.all(byMonitorPromises));

      // eslint-disable-next-line no-console
      console.log(
        "Time to load monitor counts",
        (performance.now() - beforeAllMonitors).toFixed(1),
        "ms"
      );

      if (cancellation.isCancelled) return;

      if (!selectedMonitorId) {
        setAllMonitors(monitorsWithCounts);
        setSelectedMonitor(undefined);
        setIsLoading(false);
        return;
      }

      const scopesToQuery = last.doc?.data.scope ?? [];
      const byScopeTasks = scopesToQuery.map((scope) => {
        const scopeKey = toKey(scope);
        return async () =>
          [
            scopeKey,
            await loadFindingCounts(selectedMonitorId, scopeKey),
          ] as const;
      });
      const results = await throttledAllSettled(
        byScopeTasks,
        // Firestore max limit is ~1500 before "resource exhaustion"
        { maxConcurrency: 500 }
      );
      const countsByScope: Record<string, number> = Object.fromEntries(
        results.filter(isFulfilled).map((r) => r.value)
      );

      const thisQuery = findingsQuery(
        {
          lastJob: last.doc?.data.jobId ?? "",
          monitorId: selectedMonitorId,
          state,
          scopeKey,
          assessmentPath,
          trigger: selectedTrigger,
          assignment: selectedAssignment,
          start: start ?? undefined,
          end: end ?? undefined,
        },
        limit(MAX_FINDINGS)
      );
      const selectedFindings = selectedMonitorId
        ? (await getDocs(thisQuery)).docs
        : [];

      const scopedFindings = mapWith(selectedFindings, function* (d) {
        const data = d.data();
        if (
          isInRange(new Date(data.trigger.at), start, end) &&
          (selectedTrigger !== "new" ||
            data.trigger.jobId === last.doc?.data.jobId)
        ) {
          yield { id: d.id, data, ref: d.ref } as FirestoreDoc<Finding>;
        }
      });

      const monitor = monitors?.[selectedMonitorId];
      if (!monitor) {
        setAllMonitors(monitorsWithCounts);
        setSelectedMonitor(undefined);
        setIsLoading(false);
        return;
      }

      const newSelectedMonitor: MonitorWithFindings = {
        ...monitors[selectedMonitorId],
        monitorId: selectedMonitorId,
        countsByScope,
        scopedFindings,
      };

      if (cancellation.isCancelled) return;

      setAllMonitors(monitorsWithCounts);
      setSelectedMonitor(newSelectedMonitor);
      setIsLoading(false);
    },
    [
      assessmentId,
      assessmentPath,
      end,
      last.doc?.data,
      monitors,
      refreshRequested,
      scopeKey,
      selectedMonitorId,
      start,
      state,
      selectedTrigger,
      selectedAssignment,
    ],
    staticError
  );

  const [prioritized, archived] = useMemo(() => {
    const sorted = orderBy(
      Object.values(allMonitors),
      [(i) => MonitorRanking[i.priority], "count"],
      ["asc", "desc"]
    );
    const [enabled, archived] = partition(
      sorted,
      (monitor) => !monitor.archived
    );
    const term = monitorFilter.trim().toLocaleLowerCase();
    const { type, fix, source } = filterParams;
    const shown = enabled.filter(
      (m) =>
        (m.label.toLocaleLowerCase().includes(term) ||
          priorityLabels[m.priority].toLocaleLowerCase().includes(term)) &&
        (type.length === 0 || type.includes(m.show)) &&
        (!fix || !!m.fix) &&
        (source === "all" ||
          (source === "custom" && m.isCustom) ||
          (source === "preset" && !m.isCustom))
    );
    return [shown, archived];
  }, [allMonitors, monitorFilter, filterParams]);

  const value: AssessmentFindings = useObjectMemo({
    allMonitors,
    archived,
    findingParams,
    isValidMonitorScope,
    loading: isLoading || monitorsLoading,
    monitorFilter,
    prioritized,
    range: [start ?? undefined, end ?? undefined],
    refresh,
    selectedMonitor,
    setMonitorFilter,
    setRange,
    setState,
    setTrigger,
    state: isa(FindingState, state) ? state : "open",
    trigger: selectedTrigger,
    filterParams,
    setFilterParams,
    assignment: selectedAssignment,
    setAssignment,
  });

  return (
    <FindingsContext.Provider value={value}>
      {children}
    </FindingsContext.Provider>
  );
};
